;; Copyright (C) 2017-2019, Doctor Evidence. All rights reserved.

(ns dre.shared.interface.search.blunt
  "BLUNT - BLueprint Url NoTation"
  {:authors ["Jozef Wagner"]}
  (:refer-clojure :exclude [print])
  (:require
   [clojure.string :as str]
   [dre.coll :refer [somev nn-hash-map]]
   [dre.util :refer [unkeyword into-string preserving-reduced*]]
   #?(:clj [clojure.tools.logging :refer [warn]]
      :cljs [taoensso.timbre :refer [warn]])))


(comment

;;;; Motivation

  ;; To develop a terse format for serializing query pills
  ;; into url query param value.
  ;; Goals:
  ;; - concise
  ;; - human readable
  ;; - avoid the use of unreserved URL characters in the syntax,
  ;;   to keep percent encoded characters at minimum

;;;; Specification

  ;; A pill containing its label is called a decorated pill.
  ;; Pill label is always an optional field.
  ;; If a label is not present, a dedicated endpoint can be used to
  ;; provide a canonical label for a given pill.

;;; Special characters:

  ;; ( ) , . ~
  ;; ( - beginning of the blueprint pill contents
  ;; ) - end of the blueprint pill contents
  ;; , or . - contents separator
  ;; ~ - escaping character, ~(, ~), ~,, ~~, ~.


;;; Sugar

  ;; top level a(foo,bar,baz) -> foo,bar,baz


;;; Pills

  ;; Term
  {:pill/type :term
   :pill/query "SUB5q"} ;; sense
  ;; SUB5q
  ;; k(SUB5q)

  ;; DEPRECATED
  {:pill/type :term
   :pill/query "foo"}
  ;; foo
  ;; e(foo)

  ;; DEPRECATED
  {:pill/type :term
   :pill/guid "guid"
   :pill/query "foo"} ;; :similar mode
  ;; t(guid,foo)

  ;; DEPRECATED
  {:pill/type :term
   :pill/guid "guid" ;; optional
   :pill/query "foo"
   :pill/mode :similar} ;; :descendants :ancestors ...
  ;; m(similar,guid,foo)

  ;; Tag
  {:pill/type :tag
   :pill/query "predicted/D012345"}
  ;; g(predicted/D012345)

  ;; Freetext
  {:pill/type :text
   :pill/query "foo"}
  ;; f(foo)

  ;; Boolean (AND, OR, NOT, WITH)
  '{:pill/type :and
    :pill/pills [x y z]}
  ;; a(x,y,z), top level sugar -> x,y,z

  '{:pill/type :or
    :pill/pills [x y z]}
  ;; o(x,y,z)

  '{:pill/type :with
    :pill/pills [x y z]}
  ;; w(x,y,z)

  '{:pill/type :not
    :pill/pill x}
  ;; n(x)

  ;; Category
  {:pill/type :category
   :pill/query "news"}
  ;; c(news)

  ;; URIs
  '{:pill/type :uris
    :pill/uris [uri1 uri2 uri3 ...]}
  ;; r(uri1,uri2,uri3)

  ;; Source
  {:pill/type :source
   :pill/query "medline"}
  ;; s(medline)

  ;; Author
  {:pill/type :author
   :pill/query "John Doe"}
  ;; u(John Doe)

  ;; Subject
  '{:pill/type :subject
    :pill/pills [x y]}
  ;; b(,x,y)

  '{:pill/type :subject
    :pill/pills [x y]
    :pill/tag "tag"}
  ;; b(tag,x,y)

  '{:pill/type :subject
    :pill/guid "guid"
    :pill/tag "tag"}
  ;; j(tag,guid)

  ;; Range (Date, Serial)
  {:pill/type :range
   :pill/field :changed
   :pill/start "2016-02-02"
   :pill/end nil}
  ;; d(2016-02-02,)

  {:pill/type :range
   :pill/field :id
   :pill/start nil
   :pill/end "23123123"}
  ;; i(,23123123)

  ;; Study size
  {:pill/type :study-size
   :pill/start "23"
   :pill/end "85"}
  ;; st(23,85)

  ;; Syntax
  {:pill/type :syntax
   :pill/query "NOT"}
  ;; x(NOT)

)


;;;; Implementation details

(def sep
  "A separator character."
  ".")

(def reserved
  "A set of reserved characters."
  #{\~ \( \) \, \. \space})

(defn should-escape?
  "Returns logical true if string s contains reserved characters."
  [s]
  (some reserved s))

(defn should-unescape?
  "Returns logical true if string s contains escaped characters."
  [s]
  (some #(= % \~) s))

(defn strict-throw
  [msg s pos]
  (if (and (nil? s) (nil? pos))
    (throw (ex-info msg {:type ::parsing-error :status 400}))
    (throw (ex-info (str msg " in " s " at position " pos)
                    {:s s :pos pos :type ::parsing-error :status 400}))))

(def macro-reserved
  #{\~ \( \) \, \. \s})

(defn escape
  "Returns string s with reserved characters escaped.
  Returns empty string if s is nil."
  [s]
  (let [sn (unkeyword (or s ""))]
    (cond (str/blank? sn) ""
          (should-escape? sn) (-> sn
                                  (str/replace "~" "~~")
                                  (str/replace "(" "~(")
                                  (str/replace ")" "~)")
                                  (str/replace "," "~,")
                                  (str/replace "." "~.")
                                  (str/replace " " "~s"))
          :else sn)))

(defn unescape
  "Returns string s with escaped characters unescaped.
  Returns nil if s is an empty string."
  [s]
  (cond (str/blank? s) nil
        (should-unescape? s) (-> s
                                 (str/replace "~(" "(")
                                 (str/replace "~)" ")")
                                 (str/replace "~," ",")
                                 (str/replace "~." ".")
                                 (str/replace "~s" " ")
                                 (str/replace "~~" "~"))
        :else s))

(defn contents
  "Returns a collection of strings and blueprints that
  partially print given blueprint."
  [top? blueprint]
  (let [{:keys [:pill/type :pill/query :pill/label :pill/mode
                :pill/near :pill/excluded-senses]} blueprint
        elabel (escape label)
        selabel (when-not (str/blank? elabel) (str sep elabel))
        wrap #(cons % (concat (interpose sep %2) [")"]))
        excluded-senses (set excluded-senses)
        excludef #(if (excluded-senses %) (str "x" %) %)]
    (case (keyword type)
      nil ""
      :senses
      (if (or (:pill/mode-label blueprint) (:pill/mode-args blueprint))
        (wrap "kgm(" (concat (cons (escape (excludef query))
                                   (map (comp excludef escape)
                                        (:pill/other-senses blueprint)))
                             [elabel (escape (:pill/mode blueprint))
                              (escape (:pill/mode-label blueprint))
                              (escape (:pill/mode-args blueprint))]))
        (wrap "kg(" (concat (cons (escape (excludef query))
                                  (map (comp excludef escape)
                                       (:pill/other-senses blueprint)))
                            [elabel (escape (:pill/mode blueprint))])))
      :term
      (cond (and (:pill/mode blueprint)
                 (not (= "similar" (unkeyword (:pill/mode blueprint)))))
            ["k(" (escape query) sep
             (when-not (str/blank? elabel) elabel)
             sep (escape (:pill/mode blueprint)) ")"]
            (and (:pill/mode blueprint)
                 (not (= "similar" (unkeyword (:pill/mode blueprint))))
                 (or (:pill/mode-label blueprint) (:pill/mode-args blueprint)))
            ["k(" (escape query) sep
             (when-not (str/blank? elabel) elabel)
             sep (escape (:pill/mode blueprint))
             sep (escape (:pill/mode-label blueprint))
             sep (escape (:pill/mode-args blueprint))
             ")"]
            selabel
            ["k(" (escape query) selabel ")"]
            query
            ["k(" (escape query) ")"]
            :else ["k()"])
      :tag (if selabel
             ["g(" (escape query) selabel ")"]
             ["g(" (escape query) ")"])
      :stag (if selabel
              ["h(" (escape query) selabel ")"]
              ["h(" (escape query) ")"])
      :utag (if selabel
              ["ut(" (escape query) selabel ")"]
              ["ut(" (escape query) ")"])
      :text (concat
             (when (and mode near)
               ["x(ppmnear-" near ")" sep])
             ["f(" (escape query) ")"])
      :synonyms (->> (:pill/synonyms blueprint)  ;; REGINTEL
                     (map #(str (if (:use? %) "*" "-") (:synonym %)))
                     (cons query)
                     (map escape)
                     (wrap "fs("))
      :sparql ["sparql(" (escape query) ")"]
      :and (wrap "a(" (:pill/pills blueprint))
      :or (wrap "o(" (:pill/pills blueprint))
      :with (wrap "w(" (:pill/pills blueprint))
      :not ["n(" (:pill/pill blueprint) ")"]
      :uris (wrap "r(" (map escape (:pill/uris blueprint)))
      :ai-sum-uris (wrap "air(" (map escape (:pill/uris blueprint)))
      :category (if selabel
                  ["c(" (escape query) selabel ")"]
                  ["c(" (escape query) ")"])
      (:signal :signal/unresolved) ["ss(" (escape query) ")"]
      :source (if selabel
                ["s(" (escape query) selabel ")"]
                ["s(" (escape query) ")"])
      :author (if selabel
                ["u(" (escape query) selabel ")"]
                ["u(" (escape query) ")"])
      :author-disambiguated
      (if (:pill/pill blueprint)
        ["ud(" (escape query) sep (:pill/pill blueprint) ")"]
        ["ud(" (escape query) ")"])
      :authors-search
      ["us(" (escape query) sep (:pill/pill blueprint) ")"]
      :sentence-tag
      (if (:pill/pill blueprint)
        ["gs(" (escape query) sep (:pill/pill blueprint) ")"]
        ["gs(" (escape query) ")"])
      :author-constrained
      (wrap "uc(" (cons (escape query) (:pill/pills blueprint)))
      :affiliation-constraint
      (wrap "uca(" (map escape (:pill/values blueprint)))
      :coauthor-constraint
      (wrap "ucc(" (map escape (:pill/values blueprint)))
      :email-constraint
      (wrap "uce(" (map escape (:pill/values blueprint)))
      :id-constraint
      (wrap "uci(" (map escape (:pill/values blueprint)))
      :subject (let [tag (escape (:pill/tag blueprint))]
                 ;; disable guid printing for now
                 ;; to make UI for pills
                 #_(if-let [guid (:pill/guid blueprint)]
                     ["j(" tag sep (escape guid) ")"])
                 (wrap "b(" (cons tag (:pill/pills blueprint))))
      :range [(case (unkeyword (:pill/field blueprint))
                "changed" "d("
                "published" "p("
                "id" "i("
                ;; REGINTEL
                "approved" "rx("
                "issued" "rgi("
                "patent_expire_date" "rpe("
                "submission_date" "rsd("
                "exclusivity_expiration_date" "ree("
                "orphan_exclusivity_expiration_date" "roe(")
              (escape (:pill/start blueprint))
              sep
              (escape (:pill/end blueprint))
              ")"]
      :study-size ["st("
                   (escape (:pill/start blueprint))
                   sep
                   (escape (:pill/end blueprint))
                   ")"]
      :syntax
      (do
        (assert top? "Syntax pill is top level only.")
        (cond (:pill/args blueprint)
              ["x(" (escape query) sep
               (when-not (str/blank? elabel) elabel)
               sep (escape (:pill/args blueprint)) ")"]
              label
              ["x(" (escape query) sep
               (when-not (str/blank? elabel) elabel) sep ")"]
              :else
              ["x(" (escape query) ")"]))
      :boost
      (wrap "bs(" (:pill/pills blueprint))
      ;; REGINTEL
      :same-page                 (wrap "sp(" (:pill/pills blueprint))
      :regdoc-auth-status        ["ra(" (escape query) ")"]
      :regdoc-category           ["rc(" (escape query) ")"]
      :regdoc-drug-class         ["rk(" (escape query) ")"]
      :regdoc-drug-name          ["rd(" (escape query) ")"]
      :regdoc-source             ["rs(" (escape query) ")"]
      :regdoc-substance          ["rg(" (escape query) ")"]
      :regdoc-therapeutic-area   ["rt(" (escape query) ")"]

      :regdoc-dosage-form        ["rdf(" (escape query) ")"]
      :regdoc-route              ["rro(" (escape query) ")"]
      :regdoc-presentation       ["rpr(" (escape query) ")"]
      :regdoc-applicant          ["rpc(" (escape query) ")"]
      :regdoc-marketing-status   ["rms(" (escape query) ")"]
      :regdoc-patent             ["rpn(" (escape query) ")"]
      :regdoc-bla-type           ["rbt(" (escape query) ")"]
      :regdoc-ref-drug-name      ["rrd(" (escape query) ")"]
      :regdoc-ref-substance      ["rrs(" (escape query) ")"]

      :guidance-source           ["rgs(" (escape query) ")"]
      :guidance-topic            ["rgt(" (escape query) ")"]
      :guidance-org              ["rgo(" (escape query) ")"]
      :guidance-draft            ["rgd(" (escape query) ")"]
      )))

(defn xprint
  "Returns a transducer that prints a given blueprint."
  [top?]
  (fn [rf]
    (let [prf (preserving-reduced* rf)]
      (fn
        ([] (rf))
        ([result] (rf result))
        ([result input]
         (if (string? input)
           (rf result input)
           (let [contents (contents top? input)]
             (transduce (xprint false) (completing prf)
                        result contents))))))))

(defn strict-assert
  [valid? token s]
  (when-not valid?
    (strict-throw s (:s token) (:idx token))))

(defn blueprint
  "Returns a blueprint from a given token map."
  [token strict?]
  (cond
    (string? token)
    (if strict?
      (strict-throw "Invalid string token" token 0)
      {:pill/type :term
       :pill/query token})
    (and strict? (nil? token))
    (strict-throw "Nil token in top position" nil nil)
    :else
    (let [{:keys [:contents :tag :s :idx]} token
          arg-assert #(strict-assert % token "Invalid number of arguments")
          text-assert #(strict-assert (or (nil? %) (string? %)) token "Textual argument required")
          blueprintf #(blueprint % strict?)]
      (case tag
        :k (let [[query label mode mode-label mode-args] contents]
             (arg-assert (<= 0 (count contents) 5))
             (assoc
              (nn-hash-map
               :pill/type :term
               :pill/mode (when-not (str/blank? mode) (keyword mode))
               :pill/label (when-not (str/blank? label) label)
               :pill/mode-label mode-label
               :pill/mode-args mode-args)
              :pill/query query))
        :kg (let [query (first contents)
                  [mode label] (reverse contents)
                  senses (drop-last 2 (next contents))
                  excluded? #(str/starts-with? % "x")
                  excluded (keep #(when (excluded? %) (subs % 1))
                                 (cons query senses))
                  query (if (excluded? query) (subs query 1) query)
                  senses (map #(if (excluded? %) (subs % 1) %)
                              senses)]
              (assoc
               (nn-hash-map
                :pill/type :senses
                :pill/other-senses (seq senses)
                :pill/excluded-senses excluded
                :pill/mode (when-not (str/blank? mode) (keyword mode))
                :pill/label (when-not (str/blank? label) label))
               :pill/query query))
        :kgm (let [query (first contents)
                   [mode-args mode-label mode label] (reverse contents)
                   senses (drop-last 4 (next contents))
                   excluded? #(str/starts-with? % "x")
                   excluded (keep #(when (excluded? %) (subs % 1))
                                  (cons query senses))
                   query (if (excluded? query) (subs query 1) query)
                   senses (map #(if (excluded? %) (subs % 1) %)
                               senses)]
               (assoc
                (nn-hash-map
                 :pill/type :senses
                 :pill/other-senses (seq senses)
                 :pill/excluded-senses excluded
                 :pill/mode (when-not (str/blank? mode) (keyword mode))
                 :pill/label (when-not (str/blank? label) label)
                 :pill/mode-label mode-label
                 :pill/mode-args mode-args)
                :pill/query query))
        ;; DEPRECATED
        :m (let [[mode guid query] contents]
             (arg-assert (= 3 (count contents)))
             {:pill/type :term
              :pill/mode
              (keyword (if (str/blank? mode) "similar" mode))
              :pill/guid guid :pill/query query})
        ;; DEPRECATED
        :t (let [[guid query] contents]
             (arg-assert (= 2 (count contents)))
             {:pill/type :term :pill/guid guid :pill/query query})
        ;; DEPRECATED
        :e (let [[query] contents]
             (arg-assert (= 1 (count contents)))
             {:pill/type :term :pill/query query})
        :g (let [[query label] contents]
             (arg-assert (<= 1 (count contents) 2))
             (nn-hash-map :pill/type :tag :pill/query query :pill/label label))
        :h (let [[query label] contents]
             (arg-assert (<= 1 (count contents) 2))
             (nn-hash-map :pill/type :stag :pill/query query :pill/label label))
        :gs (let [[query pill] contents
                  pill (when pill (blueprintf pill))]
              (arg-assert (>= 2 (count contents)))
              (nn-hash-map
               :pill/type :sentence-tag
               :pill/query query
               :pill/pill pill))
        :f (let [[query] contents]
             (arg-assert (= 1 (count contents)))
             (text-assert query)
             {:pill/type :text :pill/query query})
        :fs (let [[query & synonyms] contents] ;; REGINTEL
              (arg-assert (<= 2 (count contents)))
              {:pill/type :synonyms
               :pill/query query
               :pill/synonyms (mapv #(hash-map
                                      :pill/type :synonym
                                      :synonym (str/join (rest %))
                                      :use? (= \* (first %)))
                                    synonyms)})
        :sparql (let [[query] contents]
                  (arg-assert (= 1 (count contents)))
                  {:pill/type :sparql :pill/query query})
        :a (let [pills (mapv blueprintf (remove nil? contents))]
             {:pill/type :and :pill/pills pills})
        :o (let [pills (mapv blueprintf (remove nil? contents))]
             {:pill/type :or :pill/pills pills})
        :bs (let [pills (mapv blueprintf (remove nil? contents))]
              {:pill/type :boost :pill/pills pills})
        :w (let [pills (mapv blueprintf (remove nil? contents))]
             {:pill/type :with :pill/pills pills})
        :n (let [pill (when-let [x (first contents)] (blueprintf x))]
             (arg-assert (= 1 (count contents)))
             {:pill/type :not :pill/pill pill})
        :r {:pill/type :uris :pill/uris (somev contents)}
        :air {:pill/type :ai-sum-uris :pill/uris (somev contents)}
        :c (let [[query label] contents]
             (arg-assert (<= 1 (count contents) 2))
             (nn-hash-map
              :pill/type :category :pill/query query :pill/label label))
        :ss (let [[uuid] contents]
              {:pill/type :signal
               :pill/query uuid})
        :s (let [[query label] contents]
             (arg-assert (= 1 (count contents)))
             (nn-hash-map
              :pill/type :source :pill/query query :pill/label label))
        :u (let [[query label] contents]
             (arg-assert (>= 2 (count contents)))
             (nn-hash-map :pill/type :author
                          :pill/query query
                          :pill/label label))
        :ud (let [[author-query disambiguation-pill] contents
                  pill (when disambiguation-pill
                         (blueprintf disambiguation-pill))]
              (arg-assert (>= 2 (count contents)))
              (nn-hash-map
               :pill/type :author-disambiguated
               :pill/query author-query
               :pill/pill pill))
        :us (let [[author-search-query authors-search-pill] contents
                  pill (when authors-search-pill
                         (blueprintf authors-search-pill))]
              (arg-assert (>= 2 (count contents)))
              (nn-hash-map
               :pill/type :authors-search
               :pill/query author-search-query
               :pill/pill pill))
        :uc (let [[author-query & pills] contents
                  pills (mapv blueprintf (remove nil? pills))]
              {:pill/type :author-constrained
               :pill/query author-query
               :pill/pills pills})
        :uca {:pill/type :affiliation-constraint
              :pill/values (somev contents)}
        :ucc {:pill/type :coauthor-constraint
              :pill/values (somev contents)}
        :uce {:pill/type :email-constraint
              :pill/values (somev contents)}
        :uci {:pill/type :id-constraint
              :pill/values (somev contents)}
        :b (let [[tag & pills] contents
                 pills (mapv blueprintf (remove nil? pills))]
             {:pill/type :subject :pill/tag tag
              :pill/pills pills})
        :j (let [[tag guid] contents]
             (arg-assert (= 2 (count contents)))
             {:pill/type :subject :pill/tag tag
              :pill/guid guid})
        :d (let [[start end] contents]
             (arg-assert (= 2 (count contents)))
             {:pill/type :range :pill/field :changed
              :pill/start start :pill/end end})
        :p (let [[start end] contents]
             (arg-assert (= 2 (count contents)))
             {:pill/type :range :pill/field :published
              :pill/start start :pill/end end})
        :i (let [[start end] contents]
             (arg-assert (= 2 (count contents)))
             {:pill/type :range :pill/field :id
              :pill/start start :pill/end end})
        :st (let [[start end] contents]
              (arg-assert (<= 1 (count contents) 2))
              {:pill/type :study-size
               :pill/start start
               :pill/end end})
        :x (let [[query label args] contents]
             (arg-assert (<= 1 (count contents) 3))
             (nn-hash-map
              :pill/type :syntax
              :pill/query query
              :pill/label label
              :pill/args args))
        :ut (let [[uuid label] contents]
              (arg-assert (<= 1 (count contents) 3))
              (nn-hash-map
               :pill/type :utag
               :pill/query uuid
               :pill/label label))
        ;; REGINTEL
        :sp (let [pills (mapv blueprintf (remove nil? contents))]
              {:pill/type :same-page
               :pill/pills pills})
        :ra (let [[query] contents]
              (arg-assert (= 1 (count contents)))
              {:pill/type :regdoc-auth-status
               :pill/query query})
        :rc (let [[query] contents]
              (arg-assert (= 1 (count contents)))
              {:pill/type :regdoc-category
               :pill/query query})
        :rd (let [[query] contents]
              (arg-assert (= 1 (count contents)))
              {:pill/type :regdoc-drug-name
               :pill/query query})
        :rg (let [[query] contents]
              (arg-assert (= 1 (count contents)))
              {:pill/type :regdoc-substance
               :pill/query query})
        :rk (let [[query] contents]
              (arg-assert (= 1 (count contents)))
              {:pill/type :regdoc-drug-class
               :pill/query query})
        :rs (let [[query] contents]
              (arg-assert (= 1 (count contents)))
              {:pill/type :regdoc-source
               :pill/query query})
        :rt (let [[query] contents]
              (arg-assert (= 1 (count contents)))
              {:pill/type :regdoc-therapeutic-area
               :pill/query query})
        :rx (let [[start end] contents]
              (arg-assert (= 2 (count contents)))
              {:pill/type :range :pill/field :approved
               :pill/start start :pill/end end})
        :rdf (let [[query] contents]
               (arg-assert (= 1 (count contents)))
               {:pill/type :regdoc-dosage-form
                :pill/query query})
        :rro (let [[query] contents]
               (arg-assert (= 1 (count contents)))
               {:pill/type :regdoc-route
                :pill/query query})
        :rpr (let [[query] contents]
               (arg-assert (= 1 (count contents)))
               {:pill/type :regdoc-presentation
                :pill/query query})
        :rpc (let [[query] contents]
               (arg-assert (= 1 (count contents)))
               {:pill/type :regdoc-applicant
                :pill/query query})
        :rms (let [[query] contents]
               (arg-assert (= 1 (count contents)))
               {:pill/type :regdoc-marketing-status
                :pill/query query})
        :rpe (let [[start end] contents]
               (arg-assert (= 2 (count contents)))
               {:pill/type :range :pill/field :patent_expire_date
                :pill/start start :pill/end end})
        :roe (let [[start end] contents]
               (arg-assert (= 2 (count contents)))
               {:pill/type :range :pill/field :orphan_exclusivity_expiration_date
                :pill/start start :pill/end end})
        :rpn (let [[query] contents]
               (arg-assert (= 1 (count contents)))
               {:pill/type :regdoc-patent
                :pill/query query})
        :rsd (let [[start end] contents]
               (arg-assert (= 2 (count contents)))
               {:pill/type :range :pill/field :submission_date
                :pill/start start :pill/end end})
        :ree (let [[start end] contents]
               (arg-assert (= 2 (count contents)))
               {:pill/type :range :pill/field :exclusivity_expiration_date
                :pill/start start :pill/end end})
        :rbt (let [[query] contents]
               (arg-assert (= 1 (count contents)))
               {:pill/type :regdoc-bla-type
                :pill/query query})
        :rrd (let [[query] contents]
               (arg-assert (= 1 (count contents)))
               {:pill/type :regdoc-ref-drug-name
                :pill/query query})
        :rrs (let [[query] contents]
               (arg-assert (= 1 (count contents)))
               {:pill/type :regdoc-ref-substance
                :pill/query query})

        :rgs (let [[query] contents]
               (arg-assert (= 1 (count contents)))
               {:pill/type :guidance-source
                :pill/query query})
        :rgt (let [[query] contents]
               (arg-assert (= 1 (count contents)))
               {:pill/type :guidance-topic
                :pill/query query})
        :rgo (let [[query] contents]
               (arg-assert (= 1 (count contents)))
               {:pill/type :guidance-org
                :pill/query query})
        :rgd (let [[query] contents]
               (arg-assert (= 1 (count contents)))
               {:pill/type :guidance-draft
                :pill/query query})
        :rgi (let [[start end] contents]
               (arg-assert (= 2 (count contents)))
               {:pill/type :range :pill/field :issued
                :pill/start start :pill/end end})
        ;;
        (strict-throw "No matching clause" (:s token) (:idx token))
        ))))

(defn parse*
  "Returns blueprint from a given collection of tokens."
  [coll]
  (loop [coll coll
         parent nil
         current {:contents [], :tag :a}]
    (if (seq coll)
      (let [token (first coll)
            ncoll (rest coll)]
        (cond
          (= :close token)
          (recur (cons current (rest ncoll)) ;; skip nil token
                 (next parent) (first parent))
          (keyword? token)
          (recur ncoll (cons current parent)
                 {:tag token :contents []})
          :else
          (recur ncoll parent (update current :contents conj token))))
      (do (assert (nil? parent))
          (mapv #(blueprint % false) (remove nil? (:contents current)))))))

(defn pill-type-token
  [type s idx]
  {:pill-type-token true
   :tag type
   :contents []
   :s s
   :idx idx})

(defn pill-type-token?
  [m]
  (:pill-type-token m))

(defn strict-parse*
  "Returns blueprint from a given collection of tokens."
  [coll]
  (loop [coll coll
         parent nil
         current {:contents [], :tag :a}]
    (if (seq coll)
      (let [token (first coll)
            ncoll (rest coll)]
        (cond
          (= :close token)
          (recur (cons current (rest ncoll)) ;; skip nil token
                 (next parent) (first parent))
          (pill-type-token? token)
          (recur ncoll (cons current parent)
                 (dissoc token :pill-type-token))
          :else
          (recur ncoll parent (update current :contents conj token))))
      (do (when-not (nil? parent)
            (strict-throw "Invalid nesting of pills" (:s parent) (:idx parent)))
          (mapv #(blueprint % true) (remove nil? (:contents current)))))))

(defn tokenize
  "Returns a collection of tokens from a given BLUNT string."
  [s]
  (loop [macro? false
         start 0
         idx 0
         tcoll (transient [])]
    (if (= idx (count s))
      (persistent!
       (if-let [token (when (< start idx)
                        (unescape (subs s start idx)))]
         (conj! tcoll token)
         tcoll))
      (let [c #?(:cljs (.charAt s idx)
                 :clj (.charAt ^String s idx))]
        (cond (and (not macro?) (= c \~))
              (recur true start (inc idx) tcoll)
              macro?
              (recur false start (inc idx) tcoll)
              (= c \()
              (let [token (keyword (subs s start idx))
                    nidx (inc idx)]
                (recur false nidx nidx (conj! tcoll token)))
              (= c \))
              (let [token (when (< start idx)
                            (unescape (subs s start idx)))
                    nidx (inc idx)
                    ntcoll (conj! tcoll token)
                    ntcoll (conj! ntcoll :close)]
                (recur false nidx nidx ntcoll))
              (or (= c \,) (= c \.))
              (let [token (when (< start idx)
                            (unescape (subs s start idx)))
                    nidx (inc idx)
                    ntcoll (conj! tcoll token)]
                (recur false nidx nidx ntcoll))
              :else
              (recur false start (inc idx) tcoll))))))

(defn strict-tokenize
  "Returns a collection of tokens from a given BLUNT string."
  [s]
  (loop [macro? false
         start 0
         idx 0
         tcoll (transient [])]
    (if (= idx (count s))
      (persistent!
       (do (when (< start idx)
             (strict-throw "Invalid syntax for text at the end" s idx))
           tcoll))
      (let [c #?(:cljs (.charAt s idx)
                 :clj (.charAt ^String s idx))]
        (cond (and (not macro?) (= c \~))
              (recur true start (inc idx) tcoll)
              (and macro? (not (macro-reserved c)))
              (strict-throw "Invalid macro character" s idx)
              macro?
              (recur false start (inc idx) tcoll)
              (= c \()
              (let [token (if (< start idx)
                            (pill-type-token (keyword (subs s start idx)) s idx)
                            (strict-throw "Pill type missing" s idx))
                    nidx (inc idx)]
                (recur false nidx nidx (conj! tcoll token)))
              (= c \))
              (let [token (when (< start idx)
                            (unescape (subs s start idx)))
                    nidx (inc idx)
                    ntcoll (conj! tcoll token)
                    ntcoll (conj! ntcoll :close)]
                (recur false nidx nidx ntcoll))
              (or (= c \,) (= c \.))
              (let [token (when (< start idx)
                            (unescape (subs s start idx)))
                    nidx (inc idx)
                    ntcoll (conj! tcoll token)]
                (recur false nidx nidx ntcoll))
              :else
              (recur false start (inc idx) tcoll))))))

(declare print parse)

(defn valid?
  "Returns true if roundtrip returns the same object."
  [x]
  (if (string? x)
    (= x (print (parse x)))
    (= x (parse (print x)))))

(defn warn-on-percent-encoding
  [s]
  (when (and (not (str/blank? s))
             (or (str/index-of s "%2F")
                 (str/index-of s "%20")))
    (warn "Percent encoded character found in" s)))

(defn throw-on-percent-encoding
  [s]
  (when-let [pos (and (not (str/blank? s))
                      (or (str/index-of s "%2F")
                          (str/index-of s "%20")))]
    (strict-throw "Percent encoded character found"
                  s pos)))

;;;; Public API

(defn parse
  "Returns a collection of blueprints from a given BLUNT string."
  [blunt]
  (warn-on-percent-encoding blunt)
  (when-not (str/blank? blunt)
    (let [tokens (tokenize blunt)]
      (parse* tokens))))

(defn strict-parse
  "Returns a collection of blueprints from a given BLUNT string."
  [blunt]
  (throw-on-percent-encoding blunt)
  (when-not (str/blank? blunt)
    (let [tokens (strict-tokenize blunt)]
      (strict-parse* tokens))))

(defn print*
  "Returns a transducer that converts blueprint into strings
  that comprise BLUNT."
  ([]
   (print* false))
  ([top?]
   (comp (interpose sep) (xprint top?))))

(defn print
  "Returns a BLUNT string from a given blueprints."
  [blueprints]
  (into-string "" (print* true) blueprints))

;;;; Scratch

#_{:clj-kondo/ignore [:redefined-var]}
(comment

  (def x "t(D3jas323.foo).g(predicted/D1234).ba~,r.a(d(.2015-12-12).n(c(medline)).j(.s3Asadaspe3).b(.heart.fatal))")

  (def x "Breast Cancer.mild.g(predicted/D321123)")

  (def x "b().a().o().n().a(g()).c().s().d(.)")

  (def x "f(heart,,),attack")

  (strict-tokenize x)

  (parse x)

  (strict-parse x)

  (print (parse x))

  (valid? x)

  (def x
    [{:pill/type :term
      :pill/guid "a3Dfa3"
      :pill/query "foo"}
     {:pill/type :tag
      :pill/query "predicted/D1234"}
     {:pill/type :term
      :pill/query "ba,r"}
     {:pill/type :and
      :pill/pills
      [{:pill/type :range
        :pill/field :changed
        :pill/start nil
        :pill/end "2015-12-12"}
       {:pill/type :not
        :pill/pill {:pill/type :category
                    :pill/query "medline"}}
       {:pill/type :subject
        :pill/tag nil
        :pill/guid "iisad3rDD"}
       {:pill/type :subject
        :pill/tag nil
        :pill/pills [{:pill/type :term
                      :pill/query "heart"}
                     {:pill/type :term
                      :pill/query "fatal"}]}]}])

  (def x
    [{:pill/type :term
      :pill/query "Breast Cancer"}
     {:pill/type :term
      :pill/query "mild"}
     {:pill/type :tag
      :pill/query "predicted/D321123"}])

  (print x)

  (parse "k(SUFybp.Food%20Oil).p(2020-01-01.).p(.2020-01-01).x(bs).k(SUF4u.Lung)")

  (valid? x)

  (print [{:pill/type :category :pill/query "medline"}
          {:pill/type :uris :pill/uris ["123"]}])

  (print [{:pill/type :or
           :pill/pills
           [{:pill/type :term
             :pill/label "Emend"}
            {:pill/type :boost
             :pill/pills
             [{:pill/type :term
               :pill/label "Aprepitant"}]}]}])

  )
